import type { ViewjobQueryResult } from '../__generated__/viewjob.gqlgenerated';

import { useViewjobProctorAllocation } from '../../proctor/__generated__/ViewjobProctorContext';
import { DiscoveryApplyRemoveSpnTst } from '../../proctor/__generated__/ViewjobProctorSpecifications';
import { useApplyLinkQuery } from '../__generated__/applyLink.gqlgenerated';

type UseApplyLinkProps = {
    jobKey: string | null | undefined;
    viewJobData: ViewjobQueryResult['data'];
    viewJobQueryCompleted: boolean;
    url: string | null;
};

export function getApplyLinkSource(url: string | null, isApplyPlusRsvpEnabled: boolean): string {
    const indeedApplySource = url ? new URL(url).searchParams.get('indapplysrc') : null;

    // TODO: Support isDesktop for React Native Web
    const isDesktop = false;

    let source: string;
    if (indeedApplySource) {
        source = indeedApplySource;
    } else if (!isDesktop) {
        source = 'idm';
    } else {
        source = 'idd-vj';
    }

    if (isApplyPlusRsvpEnabled) {
        return source + '-ihp-apply-rsvp';
    }
    return source;
}

/**
 * Hook to fetch the Indeed Apply link URL asynchronously.
 * This query depends on data from the viewjob query (mobvjtk, continueUrl, clientMeta).
 * It runs after the viewjob query completes and does not block the main content rendering.
 */
export const useApplyLink = ({
    jobKey,
    viewJobData,
    viewJobQueryCompleted,
    url,
}: UseApplyLinkProps) => {
    const applyMethod = viewJobData?.viewjob?.details?.applyMethod;
    const indeedApplyMethod = applyMethod?.__typename === 'ViewJobIndeedApply' ? applyMethod : null;

    const source = getApplyLinkSource(url, indeedApplyMethod?.isApplyPlusRsvpEnabled === true);

    // RN-5345 (Project Veil): when discovery_apply_remove_spn_tst is active, stop sending
    // sponsoredness (`spn`) to the OneGraph applyLink query so it isn't leaked on the apply path.
    // We omit the variable entirely (undefined) rather than sending `false` — sending `false`
    // would itself disclose sponsoredness; omitting it lets OneGraph resolve sponsoredness
    // server-side. The `spn` arg is now nullable so an absent value is valid.
    const removeApplySpn = useViewjobProctorAllocation(DiscoveryApplyRemoveSpnTst).isActive;
    const spn = removeApplySpn ? undefined : (viewJobData?.viewjob?.details?.isSponsored ?? false);

    const { data, loading, error, refetch } = useApplyLinkQuery({
        variables: {
            input: jobKey ?? '',
            mobvjtk: viewJobData?.viewjob?.details?.mobvjtk ?? '',
            continueUrl: indeedApplyMethod?.continueUrl ?? '',
            spn,
            clientMeta: indeedApplyMethod?.clientMeta ?? '',
            source,
        },
        fetchPolicy: 'no-cache',
        skip: !viewJobQueryCompleted || !jobKey || !indeedApplyMethod,
    });

    const applyLinkUrl = data?.jobData?.results?.[0]?.job?.indeedApply?.applyLink?.url ?? null;

    return {
        applyLinkUrl,
        applyLinkLoading: loading,
        applyLinkError: error,
        refetchApplyLink: refetch,
    };
};
